import React, { useState, useEffect } from 'react';
import CustomerSearch from './components/CustomerSearch';
import VINInput from './components/VINInput';
import PartsDisplay from './components/PartsDisplay';
import Disclaimer from './components/Disclaimer';
import { verifyVIN, decodeSelectedPart } from './utils/api';
import './App.css';

function App() {
    const [step, setStep] = useState(1);
    const [customer, setCustomer] = useState(null);
    const [year, setYear] = useState(null);
    const [vinNumber, setVinNumber] = useState('');
    const [vinData, setVinData] = useState(null);
    const [parts, setParts] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [matchOptions, setMatchOptions] = useState(null);

    // NEW: Check for URL parameters on mount to auto-load search
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlCustomer = params.get('customer');
        const urlVin = params.get('vin');
        const urlYear = params.get('year');

        if (urlCustomer && urlVin) {
            // Auto-search from URL parameters
            autoSearchFromURL(urlCustomer, urlVin, urlYear);
        }
    }, []);

    const autoSearchFromURL = async (customerName, vin, year) => {
        setLoading(true);
        setVinNumber(vin);
        
        try {
            // Fetch customer list to get proper customer object
            const response = await fetch(`${process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api'}/customers`);
            const customerNames = await response.json();
            
            // Find matching customer
            const matchingCustomer = customerNames.find(name => 
                name.toUpperCase().includes(customerName.toUpperCase())
            );
            
            if (!matchingCustomer) {
                throw new Error('Customer not found');
            }
            
            const customerObj = { id: matchingCustomer, name: matchingCustomer };
            
            setCustomer(customerObj);
            if (year) setYear(year);
            
            const result = await verifyVIN(matchingCustomer, vin, year || '');

            if (result.multipleMatches) {
                // A deep link without a year hit a VIN with multiple records on file -
                // can't safely guess which one, so send the customer to search manually
                throw new Error('This VIN has multiple records on file - please search manually to select the correct one');
            }

            setVinData({
                part_id: result.part_id,
                model_year: result.model_year
            });
            setParts(result.parts);
            setStep(2);
        } catch (err) {
            setError(`Failed to load search: ${err.message}`);
            setStep(1);
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerSelect = (selectedCustomer) => {
        setCustomer(selectedCustomer);
        setError('');
    };

    const handleVINComplete = async (vin) => {
        if (!customer) {
            setError('Please select a customer first');
            return;
        }

        setLoading(true);
        setError('');
        setVinNumber(vin);
        setMatchOptions(null);

        try {
            const result = await verifyVIN(customer.id, vin, year);

            // Multiple distinct records found (e.g. no year given and this VIN
            // has more than one config on file) - show a picker instead of guessing
            if (result.multipleMatches) {
                setMatchOptions({
                    options: result.options,
                    customerName: result.customerName
                });
                setLoading(false);
                return;
            }

            setVinData({
                part_id: result.part_id,
                model_year: result.model_year
            });
            setParts(result.parts);
            
            // Move to results view
            setStep(2);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOptionSelect = async (option) => {
        setLoading(true);
        setError('');

        try {
            const result = await decodeSelectedPart(
                option.partId,
                matchOptions.customerName,
                option.shippingDate,
                vinNumber
            );

            setVinData({
                part_id: result.part_id,
                model_year: result.model_year
            });
            setParts(result.parts);
            setMatchOptions(null);
            setStep(2);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setStep(1);
        setCustomer(null);
        setYear(null);
        setVinNumber('');
        setVinData(null);
        setParts([]);
        setError('');
        setMatchOptions(null);
        
        // Clear URL parameters
        window.history.replaceState({}, '', window.location.pathname);
    };

    return (
        <div className="App">
            <header className="app-header">
                <h1>Chalmers VIN Lookup</h1>
                <p>Find the parts you need for your vehicle</p>
            </header>

            <div className="container">
                {step === 1 && (
                    <>
                        <div className="form">
                            <CustomerSearch onSelect={handleCustomerSelect} />
                            
                            {customer && !matchOptions && (
                                <VINInput 
                                    onComplete={handleVINComplete}
                                    disabled={loading}
                                />
                            )}

                            {matchOptions && (
                                <div className="match-options">
                                    <p className="match-options-heading">
                                        This VIN has {matchOptions.options.length} records on file.
                                        Select the correct one:
                                    </p>
                                    {matchOptions.options.map((option, idx) => (
                                        <button
                                            key={idx}
                                            className="match-option-button"
                                            onClick={() => handleOptionSelect(option)}
                                            disabled={loading}
                                        >
                                            <span className="match-option-date">
                                                {option.shippingDate || 'Date unknown'}
                                            </span>
                                            <span className="match-option-part">
                                                {option.partId}
                                            </span>
                                        </button>
                                    ))}
                                    <button
                                        className="match-options-cancel"
                                        onClick={() => setMatchOptions(null)}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}

                            {error && (
                                <div className="error-message">
                                    {error}
                                </div>
                            )}
                            
                            {loading && (
                                <div className="loading">
                                    <div className="spinner"></div>
                                    Verifying VIN...
                                </div>
                            )}
                        </div>

                        <Disclaimer />
                    </>
                )}

                {step === 2 && (
                    <PartsDisplay 
                        parts={parts} 
                        partId={vinData.part_id}
                        modelYear={vinData.model_year}
                        vinNumber={vinNumber}
                        customer={customer}
                        year={year}
                        onReset={handleReset}
                    />
                )}
            </div>
        </div>
    );
}

export default App;